import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import Switch from "~/components/formElements/Switch/Switch";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_CODE } from "~/constants/form.ts";
import useClientSelectOptions from "~/hooks/form/formOptionsData/useClientSelectOptions.ts";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import useGenderSelectOptions from "~/hooks/form/formOptionsData/useGenderSelectOptions.ts";
import useLanguageSelectOptions from "~/hooks/form/formOptionsData/useLanguageSelectOptions.ts";
import useSalesFunnelSelectOptions from "~/hooks/form/formOptionsData/useSalesFunnelSelectOptions.ts";
import useSalesTypeSelectOptions from "~/hooks/form/formOptionsData/useSalesTypeSelectOptions.ts";
import useUserSelectOptions, { employeesFilterFn } from "~/hooks/form/formOptionsData/useUsersSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import {
	updateClientContactPerson,
} from "~/modules/client/api/clientContactPerson/clientContactPersonApiDispatchers.ts";
import { clientContactPersonQueries } from "~/modules/client/api/clientContactPerson/clientContactPersonQueries.ts";
import type {
	ClientContactPersonType,
	UpdateClientContactPersonData,
} from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import ContactCompanyFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactCompanyFormSection";
import ContactContactFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactContactFormSection";
import ContactPersonalInformationFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactPersonalInformationFormSection";
import ContactSalesFormSection from "~/modules/client/components/contact/forms/formSections/ContactSalesFormSection";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import {
	preventSubmitOnEnter,
	sanitizeStringForComparison,
	transformEmptyDateValueToNull,
} from "~/utils/form/formUtils.ts";

type Props = {
	allClientsData: Client[];
	onSuccess: () => void;
	onCancel: () => void;
	contactPersonData: ClientContactPersonType;
};

interface UpdateClientContactPersonFormData extends Omit<UpdateClientContactPersonData, "newClientId" | "dateOfBirth" | "address"> {
	addressLine1: string | null;
	addressLine2: string | null;
	addressLine3: string | null;
	city: string | null;
	postCode: string | null;
	countryCode: string | null;
	dateOfBirth: Date | null;
}

const userFilterOptions = { filterFunction: employeesFilterFn };

const UpdateClientContactPersonForm: React.FC<Props> = ({ allClientsData, onSuccess, onCancel, contactPersonData }) => {
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const clientSelectOptions = useClientSelectOptions();

	const ownAddress = contactPersonData.ownAddress;

	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().default("").when("hasDeviatingAddress", {
				is: true,
				then: (fieldSchema) => fieldSchema.required(),
			}),
			addressLine2: yup.string().default(""),
			addressLine3: yup.string().default(""),
			city: yup.string().default("").when("hasDeviatingAddress", {
				is: true,
				then: (fieldSchema) => fieldSchema.required(),
			}),
			clientId: yup.string().required(),
			countryCode: yup.string().default("").when("hasDeviatingAddress", {
				is: true,
				then: (fieldSchema) => fieldSchema.required(),
			}),
			dateOfBirth: yup.date().transform(transformEmptyDateValueToNull).nullable().default(null),
			emailBusiness: yup.string().email().required().default(""),
			emailPrivate: yup.string().email().default(""),
			firstName: yup.string().required(),
			genderId: yup.string().required(),
			hasDeviatingAddress: yup.boolean().required(),
			jobTitle: yup.string().default(""),
			languageId: yup.string().required(),
			lastName: yup.string().required(),
			managedBy: yup.string().required(),
			mobilePhone: yup.string().default(""),
			nobilityTitle: yup.string().default(""),
			phone: yup.string().default(""),
			postCode: yup.string().default("").when("hasDeviatingAddress", {
				is: true,
				then: (fieldSchema) => fieldSchema.required(),
			}),
			salesFunnelId: yup.string().required(),
			salesTypeId: yup.string().required(),
			title: yup.string().default(""),
			useInformalSalutation: yup.boolean().required(),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			addressLine1: ownAddress?.addressLine1 || "",
			addressLine2: ownAddress?.addressLine2 || "",
			addressLine3: ownAddress?.addressLine3 || "",
			city: ownAddress?.city || "",
			clientId: contactPersonData.clientId,
			countryCode: ownAddress?.countryCode || DEFAULT_COUNTRY_CODE,
			dateOfBirth: contactPersonData.dateOfBirth ? new Date(contactPersonData.dateOfBirth) : null,
			emailBusiness: contactPersonData.emailBusiness || "",
			emailPrivate: contactPersonData.emailPrivate || "",
			firstName: contactPersonData.firstName,
			genderId: contactPersonData.genderId,
			hasDeviatingAddress: contactPersonData.hasDeviatingAddress,
			jobTitle: contactPersonData.jobTitle || "",
			languageId: contactPersonData.languageId,
			lastName: contactPersonData.lastName,
			managedBy: contactPersonData.managedBy,
			mobilePhone: contactPersonData.mobilePhone || "",
			nobilityTitle: contactPersonData.nobilityTitle || "",
			phone: contactPersonData.phone || "",
			postCode: ownAddress?.postCode || "",
			salesFunnelId: contactPersonData.salesFunnelId,
			salesTypeId: contactPersonData.salesTypeId,
			title: contactPersonData.title || "",
			useInformalSalutation: contactPersonData.useInformalSalutation,
		};
	}, [contactPersonData, ownAddress]);

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isSubmitted, isValid },
		watch,
		setError,
		trigger,
	} = useForm<UpdateClientContactPersonFormData>({
		mode: "onChange",
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateClientContactPersonFormData>(schema),
	});

	const countryCodeSelectOptions = useCountryCodeSelectOptions();
	const genderSelectOptions = useGenderSelectOptions();
	const languageSelectOptions = useLanguageSelectOptions();
	const managerSelectOptions = useUserSelectOptions(userFilterOptions);
	const salesFunnelSelectOptions = useSalesFunnelSelectOptions();
	const salesTypeSelectOptions = useSalesTypeSelectOptions();

	const selectedClientId = watch("clientId");
	const hasDeviatingAddress = watch("hasDeviatingAddress");

	const formIsSubmittable = useFormIsSubmittable({
		isDirty,
		isLoading: busy,
		isValid,
		isSubmitted,
		enableInitially: false,
	});

	useEffect(() => {
		trigger();
		// should trigger on load and when hasDeviatingAddress changes, to validate the address fields. This is a special
		// case because the form includes fields which were not required originally, and we therefore immediately need to
		// validate
	}, [hasDeviatingAddress, trigger]);

	const selectedClient = useMemo(() => {
		return allClientsData?.find((client) => client.id === selectedClientId);
	}, [allClientsData, selectedClientId]);

	const addressMatchesClient = (data: UpdateClientContactPersonFormData): boolean => {
		const clientAddress = selectedClient?.address;
		if (!clientAddress) return false;

		const normalize = (val: string | null | undefined) => sanitizeStringForComparison(val || "");

		return normalize(data.addressLine1) === normalize(clientAddress.addressLine1) &&
			normalize(data.addressLine2) === normalize(clientAddress.addressLine2) &&
			normalize(data.addressLine3) === normalize(clientAddress.addressLine3) &&
			normalize(data.city) === normalize(clientAddress.city) &&
			normalize(data.postCode) === normalize(clientAddress.postCode) &&
			normalize(data.countryCode) === normalize(clientAddress.countryCode);
	};

	const onSubmit: SubmitHandler<UpdateClientContactPersonFormData> = async (data) => {
		if (data.hasDeviatingAddress && addressMatchesClient(data)) {
			setError("addressLine1", { message: "Die Adresse stimmt mit der Firmenadresse überein." });
			return;
		}

		setBusy(true);
		try {
			const dateOfBirth = data.dateOfBirth ? formatDateToYYYYMMDD(new Date(data.dateOfBirth)) : null;
			const { addressLine1, addressLine2, addressLine3, city, postCode, countryCode, ...rest } = data;

			const updateData: UpdateClientContactPersonData = {
				...rest,
				dateOfBirth,
			};

			if (data.hasDeviatingAddress) {
				updateData.address = { addressLine1, addressLine2, addressLine3, city, postCode, countryCode };
			}

			if (updateData.clientId !== contactPersonData.clientId) {
				updateData.newClientId = updateData.clientId;
			}

			await updateClientContactPerson(contactPersonData.clientId, contactPersonData.id, updateData);
			await queryClient.invalidateQueries({ queryKey: clientContactPersonQueries.byId(contactPersonData.id).queryKey });
			queryClient.invalidateQueries({ queryKey: clientContactPersonQueries.byClient(contactPersonData.clientId).queryKey });
			queryClient.invalidateQueries({ queryKey: clientContactPersonQueries.all().queryKey });
			onSuccess();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Kontaktdaten aktualisieren</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<ContactCompanyFormSection<UpdateClientContactPersonFormData>
						control={control}
						clientSelectOptions={clientSelectOptions}
						errors={errors}
					/>
					<ContactPersonalInformationFormSection<UpdateClientContactPersonFormData>
						control={control}
						errors={errors}
						genderSelectOptions={genderSelectOptions}
					/>

					<ContactContactFormSection<UpdateClientContactPersonFormData>
						control={control}
					/>

					<FormSection title="Adresse">
						<Switch
							control={control}
							name="hasDeviatingAddress"
							label="Abweichende Adresse"
						/>
					</FormSection>

					{hasDeviatingAddress && (
						<AddressFormSection<UpdateClientContactPersonFormData>
							control={control}
							countryCodeSelectOptions={countryCodeSelectOptions}
							initialShowLine3={!!ownAddress?.addressLine3}
						/>
					)}

					<ContactSalesFormSection
						control={control}
						errors={errors}
						languageSelectOptions={languageSelectOptions}
						managerSelectOptions={managerSelectOptions}
						salesFunnelSelectOptions={salesFunnelSelectOptions}
						salesTypeSelectOptions={salesTypeSelectOptions}
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default UpdateClientContactPersonForm;
